<template>
  <div v-if="loader" class="h-[288px] w-full flex flex-col gap-2">
    <div class="flex items-center justify-between">
      <t-loader-skeleton w="24px" h="24px" />
      <t-loader-skeleton w="122px" h="24px" />
      <t-loader-skeleton w="24px" h="24px" />
    </div>
    <t-loader-skeleton w="100%" h="16px" />
    <div class="flex flex-col gap-2">
      <div class="flex justify-between select-none" v-for="i in 6" :key="`loader_week_${i}`">
        <div v-if="i === 1" class="flex items-center justify-between w-full h-full">
          <div class="w-8 h-8"></div>
          <div class="w-8 h-8"></div>
          <div class="w-8 h-8"></div>
          <t-loader-skeleton w="32px" h="32px" class="overflow-hidden rounded-full" v-for="j in 4" :key="`loader_day_${j}`" />
        </div>
        <div v-else-if="i === 6" class="flex items-center justify-between w-full h-full">
          <t-loader-skeleton w="32px" h="32px" class="overflow-hidden rounded-full" v-for="j in 5" :key="`loader_day_${j}`" />
          <div class="w-8 h-8"></div>
          <div class="w-8 h-8"></div>
        </div>
        <div v-else class="flex items-center justify-between w-full h-full">
          <t-loader-skeleton w="32px" h="32px" class="overflow-hidden rounded-full" v-for="j in 7" :key="`loader_day_${j}`" />
        </div>
      </div>
    </div>
  </div>
  <div v-else class="flex flex-col w-full gap-2">
    <div class="flex items-center justify-between text-black">
      <ph-caret-left
        size="24"
        class="cursor-pointer"
        @click="canAdvanceBackwards ? advanceBackwards() : ''"
        :class="{'opacity-60': !canAdvanceBackwards}"
      />
      <t-text color="black" class="font-semibold select-none">{{ selectedMonth.title }}</t-text>
      <ph-caret-right
        size="24"
        class="cursor-pointer"
        @click="canAdvanceForwards ? advanceForwards() : ''"
        :class="{'opacity-60': !canAdvanceForwards}"
      />
    </div>
    <div class="flex items-center justify-between">
      <div
        class="flex items-center justify-center w-8"
        v-for="(weekDay, idx) in $helpers.daysOfWeekLocaleArray()"
        :key="`weekDay_${idx}`"
      >
        <t-text color="gray" size="sm" class="font-semibold">{{ weekDay }}</t-text>
      </div>
    </div>
    <div class="flex flex-col gap-2">
      <div
        class="flex justify-between select-none"
        v-for="(week, idx) in parsedWeeks(_dates[selectedMonth.number])"
        :key="`week_${idx}`"
      >
        <div class="flex items-center justify-center w-8 h-8" v-for="(day, idx) in week" :key="`day_${idx}`">
          <div v-if="day === ''"></div>
          <div
            class="flex items-center justify-center w-full h-full"
            v-else-if="selectedDate.date == day.date && selectedDate.year == selectedMonth.number && selectedDate !== null"
            @click="unselectDate()"
            :class="selected.class"
            :style="selected.style"
          >
            {{ day.date }}
          </div>
          <div
            class="flex items-center justify-center w-full h-full bg-white cursor-pointer"
            v-else-if="selectedMonth.number == todayDate.year && day.date == todayDate.date && day.hasSessions"
            @click="selectDate(day.date)"
            :class="today.class"
            :style="today.style"
          >
            {{ day.date }}
          </div>
          <div
            class="flex items-center justify-center w-full h-full cursor-pointer"
            v-else-if="selectedMonth.number == todayDate.year && day.date == todayDate.date && !day.hasSessions"
            @click="selectDate(day.date)"
            :class="today.class"
            :style="today.style"
          >
            {{ day.date }}
          </div>
          <div
            class="flex items-center justify-center w-full h-full cursor-pointer"
            v-else-if="day.hasSessions"
            @click="selectDate(day.date)"
            :class="available.class"
            :style="available.style"
          >
            {{ day.date }}
          </div>
          <div
            class="flex items-center justify-center w-full h-full cursor-pointer"
            v-else-if="!day.hasSessions"
            @click="selectDate(day.date)"
            :class="disabled.class"
          >
            {{ day.date }}
          </div>
          <div v-else>{{ day.date }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  export default {
    name: 't-calendar',
    computed: {
      ...mapGetters(['themeColorHEX']),
      selected() {
        return {
          style: {
            background: this.$helpers.getCorrectColor(this.themeColorHEX)
          },
          class: 'text-white rounded-full'
        }
      },
      available() {
        return {
          style: {
            color: this.$helpers.getCorrectColor(this.themeColorHEX)
          },
          class: `bg-white rounded-full`
        }
      },
      disabled() {
        return {class: `text-gray rounded-full`}
      },
      today() {
        return {
          style: {
            outlineColor: this.$helpers.getCorrectColor(this.themeColorHEX),
            color: this.$helpers.getCorrectColor(this.themeColorHEX),
            outlineStyle: 'solid',
            outlineOffset: '-2px'
          },
          class: `outline-2 rounded-full`
        }
      },
      _dates() {
        const result = {}
        const allDates =
          this.dates.length > 0
            ? this.dates.slice().sort()
            : [
                `${new Date().getFullYear()}-${
                  new Date().getMonth() + 1 < 10 ? `0${new Date().getMonth() + 1}` : new Date().getMonth() + 1
                }-${new Date().getDate() < 10 ? `0${new Date().getDate()}` : new Date().getDate()}`
              ]

        const startDate = new Date(allDates[0])
        const endDate = new Date(allDates[allDates.length - 1])

        let currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1)
        while (currentDate <= endDate) {
          const year = currentDate.getFullYear()
          const month = currentDate.getMonth() + 1
          const key = `${year}-${month < 10 ? '0' + month : month}`
          const lastDayOfMonth = new Date(year, month, 0).getDate()

          result[key] = []
          for (let day = 1; day <= lastDayOfMonth; day++) {
            const paddedDay = day < 10 ? `0${day}` : `${day}`
            result[key].push({date: `${year}-${month < 10 ? '0' + month : month}-${paddedDay}`, hasSessions: false})
          }

          currentDate.setMonth(currentDate.getMonth() + 1)
        }

        allDates.forEach(date => {
          const [year, month, day] = date.split('-')
          const key = `${year}-${month}`
          result[key][parseInt(day) - 1] = {date, hasSessions: true}
        })

        return result
      },
      todayDate() {
        const date = new Date()
        return {
          year: `${date.getFullYear()}-${date.getMonth() < 9 ? `0${date.getMonth() + 1}` : date.getMonth()}`,
          date: date.getDate()
        }
      },
      canAdvanceBackwards() {
        const idx = Object.keys(this._dates).indexOf(this.selectedMonth.number)
        return idx !== 0
      },
      canAdvanceForwards() {
        const idx = Object.keys(this._dates).indexOf(this.selectedMonth.number)
        return idx !== Object.keys(this._dates).length - 1
      }
    },
    props: {
      dates: {
        type: Array,
        required: true
      },
      preselectedDate: {
        type: String
      }
    },
    data() {
      return {
        selectedMonth: {
          number: null,
          title: null
        },
        selectedDate: {
          date: null,
          year: null
        },
        loader: true
      }
    },
    async created() {
      await this.dataFetched()
      this.loader = false
      this.setSelectedMonth(this.preselectedDate ? this.preselectedDate : this.getClosestDay(this.dates))
    },
    methods: {
      parsedWeeks(dates) {
        if (!dates) return
        const result = []
        let currentWeek = []

        for (let i = 0; i < dates.length; i++) {
          const date = new Date(dates[i].date)
          const dayOfMonth = date.getDate()
          const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1

          // Check if it's the beginning of the month
          if (dayOfMonth === 1) {
            for (let j = 0; j < dayOfWeek; j++) {
              currentWeek.push('')
            }
          }
          // Push the current date to the current week array
          currentWeek.push({
            date: Array.from(dates[i].date.split('-')[2])
              .map((el, idx) => (el == 0 && idx == 0 ? '' : el))
              .join(''),
            hasSessions: dates[i].hasSessions
          })

          // Check if it's the end of the week or the end of the month
          if (currentWeek.length === 7 || i === dates.length - 1) {
            while (currentWeek.length < 7) {
              currentWeek.push('')
            }
            result.push(currentWeek)
            currentWeek = []
          }
        }

        while (result.length < 6) {
          result.push(Array(7).fill(''))
        }

        return result
      },
      setSelectedMonth(date = null) {
        let months = []
        if (this.$i18n.locale === 'uk_UA') {
          months = [
            'Січень',
            'Лютий',
            'Березень',
            'Квітень',
            'Травень',
            'Червень',
            'Липень',
            'Серпень',
            'Вересень',
            'Жовтень',
            'Листопад',
            'Грудень'
          ]
        } else if (this.$i18n.locale === 'ko_KR') {
          months = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월']
        } else if (this.$i18n.locale === 'cs_CZ') {
          months = [
            'Leden',
            'Únor',
            'Březen',
            'Duben',
            'Květen',
            'Červen',
            'Červenec',
            'Srpen',
            'Září',
            'Říjen',
            'Listopad',
            'Prosinec'
          ]
        } else if (this.$i18n.locale === 'nl_NL') {
          months = [
            'Januari',
            'Februari',
            'Maart',
            'April',
            'Mei',
            'Juni',
            'Juli',
            'Augustus',
            'September',
            'Oktober',
            'November',
            'December'
          ]
        } else if (this.$i18n.locale === 'fr_FR') {
          months = [
            'Janvier',
            'Février',
            'Mars',
            'Avril',
            'Mai',
            'Juin',
            'Juillet',
            'Août',
            'Septembre',
            'Octobre',
            'Novembre',
            'Décembre'
          ]
        } else if (this.$i18n.locale === 'de_DE') {
          months = [
            'Januar',
            'Februar',
            'März',
            'April',
            'Mai',
            'Juni',
            'Juli',
            'August',
            'September',
            'Oktober',
            'November',
            'Dezember'
          ]
        } else if (this.$i18n.locale === 'it_IT') {
          months = [
            'Gennaio',
            'Febbraio',
            'Marzo',
            'Aprile',
            'Maggio',
            'Giugno',
            'Luglio',
            'Agosto',
            'Settembre',
            'Ottobre',
            'Novembre',
            'Dicembre'
          ]
        } else if (this.$i18n.locale === 'pl_PL') {
          months = [
            'Styczeń',
            'Luty',
            'Marzec',
            'Kwiecień',
            'Maj',
            'Czerwiec',
            'Lipiec',
            'Sierpień',
            'Wrzesień',
            'Październik',
            'Listopad',
            'Grudzień'
          ]
        } else if (this.$i18n.locale === 'pt_PT') {
          months = [
            'Janeiro',
            'Fevereiro',
            'Março',
            'Abril',
            'Maio',
            'Junho',
            'Julho',
            'Agosto',
            'Setembro',
            'Outubro',
            'Novembro',
            'Dezembro'
          ]
        } else if (this.$i18n.locale === 'es_ES') {
          months = [
            'Enero',
            'Febrero',
            'Marzo',
            'Abril',
            'Mayo',
            'Junio',
            'Julio',
            'Agosto',
            'Septiembre',
            'Octubre',
            'Noviembre',
            'Diciembre'
          ]
        } else if (this.$i18n.locale === 'sv_SE') {
          months = [
            'Januari',
            'Februari',
            'Mars',
            'April',
            'Maj',
            'Juni',
            'Juli',
            'Augusti',
            'September',
            'Oktober',
            'November',
            'December'
          ]
        } else if (this.$i18n.locale === 'ru_RU') {
          months = ['Январь', 'Ферваль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
        } else if (this.$i18n.locale === 'lt_LT') {
          months = [
            'Sausis',
            'Vasaris',
            'Kovas',
            'Balandis',
            'Gegužė',
            'Birželis',
            'Liepa',
            'Rugpjūtis',
            'Rugsėjis',
            'Spalis',
            'Lapkritis',
            'Gruodis'
          ]
        } else {
          months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December'
          ]
        }

        const currentDate = date ? new Date(date) : new Date()
        const month = months[currentDate.getMonth()]
        const year = currentDate.getFullYear()

        this.selectedMonth.title = `${month} ${year}`
        this.selectedMonth.number =
          currentDate.getMonth() < 9 ? `${year}-0${currentDate.getMonth() + 1}` : `${year}-${currentDate.getMonth() + 1}`

        if (this.preselectedDate) {
          this.selectedDate = {
            date: currentDate.getDate(),
            year: currentDate.getMonth() < 9 ? `${year}-0${currentDate.getMonth() + 1}` : `${year}-${currentDate.getMonth() + 1}`
          }
        }
      },
      getClosestDay(dates) {
        const currentDate = new Date()

        const dateObjects = dates.map(date => new Date(date))

        const futureDates = dateObjects.filter(date => date > currentDate)

        if (futureDates.length > 0) {
          const nearestFutureDate = new Date(Math.min.apply(null, futureDates))
          return nearestFutureDate.toISOString().split('T')[0]
        } else {
          const nearestPastDate = new Date(Math.max.apply(null, dateObjects))
          return nearestPastDate.toISOString().split('T')[0]
        }
      },
      selectDate(date) {
        this.selectedDate.year = this.selectedMonth.number
        this.selectedDate.date = date
        this.$emit(
          'dayChange',
          `${this.selectedDate.year}-${this.selectedDate.date < 10 ? `0${this.selectedDate.date}` : this.selectedDate.date}`
        )
      },
      unselectDate() {
        this.selectedDate.year = null
        this.selectedDate.date = null
        this.$emit('dayChange', null)
      },
      advanceBackwards() {
        const idx = Object.keys(this._dates).indexOf(this.selectedMonth.number)
        this.setSelectedMonth(Object.keys(this._dates)[idx - 1], false)
      },
      advanceForwards() {
        const idx = Object.keys(this._dates).indexOf(this.selectedMonth.number)
        this.setSelectedMonth(Object.keys(this._dates)[idx + 1], false)
      },
      async dataFetched() {
        return new Promise(resolve => {
          let timeConsumed = 0
          const interval = setInterval(() => {
            if (this.dates.length !== 0) {
              clearInterval(interval)
              resolve()
            }
            if (timeConsumed >= 30000) clearInterval(interval)
            timeConsumed += 333
          }, 333)
        })
      }
    },
    watch: {
      dates() {
        if (this.loader) return
        this.loader = true
        this.setSelectedMonth(this.getClosestDay(this.dates))
        setTimeout(() => (this.loader = false), 500)
      }
    }
  }
</script>
